import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  LightMode,
  DarkMode,
  SettingsBrightness,
  Palette,
  Close,
} from "@mui/icons-material";
import { useTheme } from "../../contexts/ThemeContext";
import { ThemeMode } from "../../contexts/ThemeContext";

interface ThemeSettingsProps {
  onClose?: () => void;
  compact?: boolean;
}

const ThemeSettings: React.FC<ThemeSettingsProps> = ({
  onClose,
  compact = false,
}) => {
  const { themeMode, actualTheme, setThemeMode, toggleTheme } = useTheme();
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [isAnimating, setIsAnimating] = useState(false);

  const handleThemeChange = (
    event: React.MouseEvent<HTMLElement>,
    newTheme: ThemeMode | null
  ) => {
    if (newTheme !== null && newTheme !== themeMode) {
      setIsAnimating(true);
      setThemeMode(newTheme);

      // Reset animation state
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleQuickToggle = () => {
    setIsAnimating(true);
    toggleTheme();
    setTimeout(() => setIsAnimating(false), 300);
  };

  const getThemeIcon = (mode: ThemeMode) => {
    switch (mode) {
      case "light":
        return <LightMode />;
      case "dark":
        return <DarkMode />;
      case "system":
        return <SettingsBrightness />;
      default:
        return <SettingsBrightness />;
    }
  };

  const getThemeLabel = (mode: ThemeMode) => {
    switch (mode) {
      case "light":
        return "Light";
      case "dark":
        return "Dark";
      case "system":
        return "System";
      default:
        return "System";
    }
  };

  const getThemeDescription = (mode: ThemeMode) => {
    switch (mode) {
      case "light":
        return "Always use light theme";
      case "dark":
        return "Always use dark theme";
      case "system":
        return `Follow system preference (currently ${systemPrefersDark ? "dark" : "light"})`;
      default:
        return "Follow system preference";
    }
  };

  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip
          title={`Switch to ${actualTheme === "dark" ? "light" : "dark"} theme`}
        >
          <IconButton
            onClick={handleQuickToggle}
            sx={{
              color: "text.primary",
              transform: isAnimating ? "scale(0.9)" : "scale(1)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: "action.hover",
                transform: "scale(1.1)",
              },
            }}
          >
            {actualTheme === "dark" ? <LightMode /> : <DarkMode />}
          </IconButton>
        </Tooltip>

        <Chip
          label={getThemeLabel(themeMode)}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.75rem",
            height: 24,
            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(22, 27, 34, 0.95) 0%, rgba(30, 36, 47, 0.95) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)",
        backdropFilter: "blur(20px)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(240, 246, 252, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.06)",
        position: "relative",
        minWidth: 300,
        maxWidth: 400,
      }}
    >
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "text.secondary",
          }}
        >
          <Close />
        </IconButton>
      )}

      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Palette color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Appearance
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Customize how the app looks on your device
          </Typography>
        </Box>

        <Divider />

        {/* Quick Toggle */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            Quick Toggle
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={actualTheme === "dark"}
                onChange={handleQuickToggle}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "primary.main",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "primary.main",
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Dark Mode
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Currently using {actualTheme} theme
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider />

        {/* Theme Selection */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            Theme Preference
          </Typography>

          <ToggleButtonGroup
            value={themeMode}
            exclusive
            onChange={handleThemeChange}
            orientation="vertical"
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(240, 246, 252, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.06)",
                borderRadius: 2,
                p: 2,
                justifyContent: "flex-start",
                textTransform: "none",
                "&:not(:last-of-type)": {
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(240, 246, 252, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.06)",
                },
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
            }}
          >
            {(["light", "dark", "system"] as ThemeMode[]).map((mode) => (
              <ToggleButton key={mode} value={mode}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  width="100%"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor:
                        themeMode === mode
                          ? "rgba(255, 255, 255, 0.2)"
                          : "action.hover",
                      transition: "all 0.2s",
                    }}
                  >
                    {getThemeIcon(mode)}
                  </Box>
                  <Box sx={{ textAlign: "left", flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {getThemeLabel(mode)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getThemeDescription(mode)}
                    </Typography>
                  </Box>
                </Stack>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Current Status */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: "action.hover",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(240, 246, 252, 0.05)"
                : "1px solid rgba(0, 0, 0, 0.03)",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 1.5,
                backgroundColor: actualTheme === "dark" ? "#1C2128" : "#F8F9FA",
                border: (theme) => `2px solid ${theme.palette.primary.main}`,
                color: "primary.main",
              }}
            >
              {actualTheme === "dark" ? (
                <DarkMode fontSize="small" />
              ) : (
                <LightMode fontSize="small" />
              )}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Currently Active
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {actualTheme === "dark" ? "Dark" : "Light"} theme
                {themeMode === "system" &&
                  ` (system ${systemPrefersDark ? "dark" : "light"})`}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ThemeSettings;
